import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV, COMPANY } from "@/data/content";

const Logo = () => (
  <Link to="/" data-testid="nav-logo" className="flex flex-col leading-[0.82] select-none">
    <span className="font-heading font-extrabold tracking-tight text-lg text-raritan-red">
      {COMPANY.logoTop}
    </span>
    <span className="font-heading font-extrabold tracking-tight text-lg text-raritan-green">
      {COMPANY.logoBottom}
    </span>
  </Link>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <header
        data-testid="site-navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 border-b ${
          scrolled ? "bg-raritan-bg/90 backdrop-blur-md border-raritan-border" : "bg-transparent border-transparent"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-[68px] flex items-center justify-between">
          <Logo />

          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((n) => {
              const active = location.pathname === n.to;
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`nav-link-${n.label.toLowerCase()}`}
                  className="group relative px-2.5 py-2 font-mono text-[11px] uppercase tracking-[0.12em] text-raritan-ink"
                >
                  <span className={active ? "text-raritan-green" : "group-hover:text-raritan-green transition-colors"}>
                    {n.label}
                  </span>
                  <span
                    className={`absolute left-2.5 -bottom-0.5 h-px bg-raritan-red transition-all duration-300 ${
                      active ? "w-4" : "w-0 group-hover:w-4"
                    }`}
                  />
                </Link>
              );
            })}
          </nav>

          <Link
            to="/contact"
            data-testid="nav-cta"
            className="hidden lg:inline-flex items-center gap-2 bg-raritan-green text-white px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] hover:bg-raritan-green-hover transition-colors group"
          >
            Enquire
            <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </Link>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden text-raritan-ink"
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            className="fixed inset-0 top-[68px] z-40 bg-raritan-bg lg:hidden overflow-y-auto"
          >
            <div className="flex flex-col px-6 py-6">
              {NAV.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`mobile-nav-link-${n.label.toLowerCase()}`}
                  className="py-4 border-b border-raritan-border font-heading text-2xl font-bold text-raritan-ink"
                >
                  {n.label}
                </Link>
              ))}
              <Link
                to="/contact"
                className="mt-6 bg-raritan-green text-white text-center py-4 font-mono text-xs uppercase tracking-[0.15em]"
              >
                Enquire Now
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
